import React, { useEffect, useMemo } from 'react'
import { ThemeProvider } from '@emotion/react'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style as StatusBarStyle } from '@capacitor/status-bar'
import { useAppDispatch, useAppSelector } from './redux/hooks'
import { RenderMedia, RenderPlatform } from '@fusion-advantage/backcompat'
import { fusionPrintThemeOverrides, V2L1ModalUnderlay, V2L5AppIntro, V2L5LoadingOverlay } from '@fusion-advantage/formrender'
import { Theme } from '@emotion/react'
import ScanOverlay from './stories/content/ScanOverlay'
import { Outlet } from 'react-router'
import { updateRenderState } from './lib/relay/updateRenderState'
import { statusBarHeightPx } from './style'
import L4SideNavContainer from './stories/chrome/l4/L4SideNavContainer'
import L4T1NavTrigger from './stories/chrome/l4/L4T1NavTrigger'
import { LxLogModal } from './stories/content/modal/LxLogModal'
import { getLongCopyright } from './lib/appInfo'
import { AppModal, DocgenStatus, incrementBackgroundTaps } from './redux/appSlice'
import { LxDevTokenModal } from './stories/content/modal/LxDevTokenModal'
import { LxDatabaseModal } from './stories/content/modal/LxDatabaseModal'
import { LxCancelEncounterModal } from './stories/content/modal/LxCancelEncounterModal'
import { LxSyncModal } from './stories/content/modal/LxSyncModal'
import { LxManageEncountersModal } from './stories/content/modal/LxManageEncountersModal'
import { LxLocationModal } from './stories/content/modal/LxLocationModal'
import { LxGenerateMeetingLinksModal } from './stories/content/modal/LxGenerateMeetingLinksModal'
import { LxFeedbackModal } from './stories/content/modal/LxFeedbackModal'
import { useForegroundedHalfScreenBugFix } from './lib/useForegroundedHalfScreenBugFix'
import { usePendingSyncLeaveGuard } from './lib/usePendingSyncLeaveGuard'

const AppRoot = React.memo(() => {
	const booted = useAppSelector(state => state.app.booted)
	const modal = useAppSelector(state => state.app.modals.at(-1))
	const loading = useAppSelector(state => Boolean(state.app.ldng.form))
	const media = useAppSelector(state => state.render.media)
	const renderTheme = useAppSelector(state => state.render.theme)
	const barcodeScannerOpen = useAppSelector(state => state.app.barcodeScannerOpen)
	const dispatch = useAppDispatch()
	useForegroundedHalfScreenBugFix()
	usePendingSyncLeaveGuard()
	useEffect(() => {
		if(Capacitor.isNativePlatform()) {
			StatusBar.setStyle({ style: StatusBarStyle.Dark })
		}
		dispatch(updateRenderState({
			platform: Capacitor.isNativePlatform() ? RenderPlatform.Native : RenderPlatform.Web,
			theme: {
				// wether they are on a native device with status bar or not
				// doesn't change, so this doesn't need to be reactive.
				// even though the consuming <App /> component sets this also,
				// we dispatch it here so that formrender matches.
				statusBarHeightPx: statusBarHeightPx(),
			},
		}))
		const handleBodyClick = () => dispatch(incrementBackgroundTaps())
		const handleKeyDown = (e: KeyboardEvent) => {
			if(e.key === 'Escape') {
				handleBodyClick()
			}
		}
		document.body.addEventListener('click', handleBodyClick)
		document.body.addEventListener('keydown', handleKeyDown)
		return () => {
			document.body.removeEventListener('click', handleBodyClick)
			document.body.removeEventListener('keydown', handleKeyDown)
		}
	}, [])
	const theme = useMemo((): Partial<Theme> => {
		return {
			...(media === RenderMedia.Print ? fusionPrintThemeOverrides : {}),
			...renderTheme,
		}
	}, [media, renderTheme])
	if(!booted) {
		// appBoot happens in a couple ms,
		// at which point LxDashboard takes over.
		// in the mean time, don't show a loading indicator
		// in case something fails hard
		return (
			<V2L5AppIntro
				loading={false}
				canLogin={false}
				copyright={getLongCopyright()}
			/>
		)
	}
	return (
		<ThemeProvider theme={theme}>
			{booted ? <ScanOverlay visible={barcodeScannerOpen} /> : null}
			<Outlet />
			<L4SideNavContainer />
			<L4T1NavTrigger />
			{modal ? (
				<V2L1ModalUnderlay>
					{modal === AppModal.CancelEncounter ? <LxCancelEncounterModal /> : null}
					{modal === AppModal.Database ? <LxDatabaseModal /> : null}
					{modal === AppModal.DevToken ? <LxDevTokenModal /> : null}
					{modal === AppModal.ManageEncounters ? <LxManageEncountersModal /> : null}
					{modal === AppModal.GenerateMeetingLinks ? <LxGenerateMeetingLinksModal /> : null}
					{modal === AppModal.Feedback ? <LxFeedbackModal /> : null}
					{modal === AppModal.Location ? <LxLocationModal /> : null}
					{modal === AppModal.Log ? <LxLogModal /> : null}
					{modal === AppModal.Sync ? <LxSyncModal /> : null}
				</V2L1ModalUnderlay>
			) : null}
			<V2L5LoadingOverlay loading={loading} />
			<DocgenLoadStatusFlag />
		</ThemeProvider>
	)
})

export default AppRoot

const DocgenLoadStatusFlag = () => {
	const docgenState = useAppSelector(state => state.app.docgen)
	let id
	let printHeight: number|undefined
	switch(docgenState.status) {
		case DocgenStatus.Success:
			id = "print-loading-success"
			printHeight = docgenState.height
			break
		case DocgenStatus.Loading:
			// id = "print-loading"
			// break
			return null
		case DocgenStatus.Error:
			id = "print-loading-error"
			break
	}
	return <div style={{display: 'none'}} id={id} data-print-height={printHeight} />
}
